import React from "react";
import { motion } from "framer-motion";
import { PROFILE, RESUME_URL } from "../lib/data";
import { ArrowDown, Download, ArrowUpRight } from "lucide-react";

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden">
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 0.07, scale: 1 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
        className="absolute -top-32 -right-40 w-[42rem] h-[42rem] rounded-full"
        style={{ background: "radial-gradient(circle, hsl(242 47% 34%) 0%, transparent 70%)" }}
      />
      <div className="max-w-6xl mx-auto px-6 w-full relative z-10">
        <motion.p {...fade(0.05)} className="overline text-primary mb-6">
          Senior UX Designer · {PROFILE.yearsExperience} Years
        </motion.p>

        <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight text-foreground">
          <MaskLine delay={0.12}>Vatushi</MaskLine>
          <MaskLine delay={0.24} className="text-primary">Moondra.</MaskLine>
        </h1>

        <motion.p {...fade(0.7)} className="mt-8 text-lg md:text-xl text-foreground/70 max-w-2xl leading-relaxed">
          {PROFILE.headline}
        </motion.p>

        <motion.div {...fade(0.85)} className="mt-10 flex flex-wrap items-center gap-4">
          <a href="#projects" data-testid="view-projects-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-[hsl(242,47%,28%)] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
            View Projects
            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a href={RESUME_URL} target="_blank" rel="noopener noreferrer" download data-testid="hero-resume-btn"
            className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-6 py-3 text-sm font-medium text-foreground hover:border-primary hover:text-primary transition-colors duration-200">
            <Download size={16} /> Download Resume
          </a>
        </motion.div>

        <motion.div {...fade(1.05)} className="mt-24 flex items-center gap-3 text-foreground/40">
          <ArrowDown size={16} className="animate-bounce" />
          <span className="overline">Scroll to explore</span>
        </motion.div>
      </div>
    </section>
  );
};
